import { supabase } from './supabase';
import { logger } from '../utils/logger';

/**
 * Check if two users have blocked each other (either direction)
 */
export async function areUsersBlocked(userId1: string, userId2: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('blocked_users')
      .select('id')
      .or(`and(blocker_id.eq.${userId1},blocked_id.eq.${userId2}),and(blocker_id.eq.${userId2},blocked_id.eq.${userId1})`);

    if (error) {
      logger.error('Error checking block status:', error);
      return false;
    }

    return (data?.length || 0) > 0;
  } catch (error) {
    logger.error('Error in areUsersBlocked:', error);
    return false;
  }
}

/**
 * Check if current user has blocked or been blocked by a target user
 */
export async function isBlockedRelationship(targetUserId: string): Promise<{ blocked: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { blocked: false, error: 'Not authenticated' };
    }

    const blocked = await areUsersBlocked(user.id, targetUserId);
    return { blocked };
  } catch (error) {
    logger.error('Error in isBlockedRelationship:', error);
    return { blocked: false, error: 'Failed to check block status' };
  }
}

/**
 * Filter out users who have blocked or been blocked by the current user
 */
export async function filterBlockedUsers(userIds: string[]): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || userIds.length === 0) {
      return userIds;
    }

    // Get all blocks involving current user
    const { data: blocks, error } = await supabase
      .from('blocked_users')
      .select('blocker_id, blocked_id')
      .or(`blocker_id.eq.${user.id},blocked_id.eq.${user.id}`);

    if (error) {
      logger.error('Error filtering blocked users:', error);
      return userIds;
    }

    if (!blocks || blocks.length === 0) {
      return userIds;
    }

    // Create set of blocked user IDs
    const blockedIds = new Set<string>();
    blocks.forEach(block => {
      if (block.blocker_id === user.id) {
        blockedIds.add(block.blocked_id);
      } else {
        blockedIds.add(block.blocker_id);
      }
    });

    // Filter out blocked users
    return userIds.filter(id => !blockedIds.has(id));
  } catch (error) {
    logger.error('Error in filterBlockedUsers:', error);
    return userIds;
  }
}

/**
 * Check if a user can be added to a group (not blocked by any existing members)
 */
export async function canAddToGroup(groupId: string, newUserId: string): Promise<{ allowed: boolean; blockedBy?: string; error?: string }> {
  try {
    // Get all current group members
    const { data: members, error: membersError } = await supabase
      .from('group_members')
      .select('user_id')
      .eq('group_id', groupId);

    if (membersError) {
      logger.error('Error fetching group members:', membersError);
      return { allowed: false, error: membersError.message };
    }

    if (!members || members.length === 0) {
      return { allowed: true };
    }

    const memberIds = members.map(m => m.user_id);

    // Check if new user is blocked by or has blocked any member
    const { data: blocks, error: blocksError } = await supabase
      .from('blocked_users')
      .select('blocker_id, blocked_id')
      .or(
        memberIds.map(id => `and(blocker_id.eq.${id},blocked_id.eq.${newUserId}),and(blocker_id.eq.${newUserId},blocked_id.eq.${id})`).join(',')
      );

    if (blocksError) {
      logger.error('Error checking blocks:', blocksError);
      return { allowed: false, error: blocksError.message };
    }

    if (blocks && blocks.length > 0) {
      const blocker = blocks[0].blocker_id;
      return { allowed: false, blockedBy: blocker };
    }

    return { allowed: true };
  } catch (error) {
    logger.error('Error in canAddToGroup:', error);
    return { allowed: false, error: 'Failed to check block status' };
  }
}

/**
 * Check if a user can be invited to a session (not blocked by host or other participants)
 */
export async function canInviteToSession(sessionId: string, newUserId: string): Promise<{ allowed: boolean; blockedBy?: string; error?: string }> {
  try {
    // Get session host and current participants
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select(`
        host_id,
        participants:session_participants(user_id)
      `)
      .eq('id', sessionId)
      .single();

    if (sessionError) {
      logger.error('Error fetching session:', sessionError);
      return { allowed: false, error: sessionError.message };
    }

    if (!session) {
      return { allowed: false, error: 'Session not found' };
    }

    // Collect all user IDs (host + participants)
    const userIds = [session.host_id];
    if (session.participants) {
      userIds.push(...session.participants.map((p: any) => p.user_id));
    }

    // Remove duplicates
    const uniqueUserIds = [...new Set(userIds)];

    // Check if new user is blocked by or has blocked anyone
    const { data: blocks, error: blocksError } = await supabase
      .from('blocked_users')
      .select('blocker_id, blocked_id')
      .or(
        uniqueUserIds.map(id => `and(blocker_id.eq.${id},blocked_id.eq.${newUserId}),and(blocker_id.eq.${newUserId},blocked_id.eq.${id})`).join(',')
      );

    if (blocksError) {
      logger.error('Error checking blocks:', blocksError);
      return { allowed: false, error: blocksError.message };
    }

    if (blocks && blocks.length > 0) {
      const blocker = blocks[0].blocker_id;
      return { allowed: false, blockedBy: blocker };
    }

    return { allowed: true };
  } catch (error) {
    logger.error('Error in canInviteToSession:', error);
    return { allowed: false, error: 'Failed to check block status' };
  }
}

/**
 * Get list of user IDs that current user has blocked or been blocked by
 */
export async function getBlockedUserIds(): Promise<{ userIds: string[]; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { userIds: [], error: 'Not authenticated' };
    }

    const { data: blocks, error } = await supabase
      .from('blocked_users')
      .select('blocker_id, blocked_id')
      .or(`blocker_id.eq.${user.id},blocked_id.eq.${user.id}`);

    if (error) {
      logger.error('Error fetching blocked users:', error);
      return { userIds: [], error: error.message };
    }

    if (!blocks || blocks.length === 0) {
      return { userIds: [] };
    }

    const blockedIds = new Set<string>();
    blocks.forEach(block => {
      if (block.blocker_id === user.id) {
        blockedIds.add(block.blocked_id);
      } else {
        blockedIds.add(block.blocker_id);
      }
    });

    return { userIds: Array.from(blockedIds) };
  } catch (error) {
    logger.error('Error in getBlockedUserIds:', error);
    return { userIds: [], error: 'Failed to fetch blocked users' };
  }
}